import { Box, Skeleton } from '@mui/material';
import agent from '@root/agent';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
import ContentBox from '@root/components/v2/content/ContentBox';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { RiskTypeIcons } from '@root/pipes/riskTypeIcons';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { stackedLayersColumn } from '../CVEs/k8Columns';
import Text from '@root/components/v2/ui-elements/Text';

const CommandLine = ({ command, index, onCopy, copiedIndex }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      onCopy(index);
    } catch (err) {
      console.error('Failed to copy command:', err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 16px', // Increased padding
        minHeight: '50px', // Increased minimum height
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',

        borderRadius: '8px',
        fontSize: '13px',
        fontFamily: 'Monaco, "Cascadia Code", "Roboto Mono", monospace',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#f1f5f9',
          borderColor: '#cbd5e1',
        },
        position: 'relative',
        cursor: 'pointer',
        overflow: 'hidden', // Fix x-axis overflow
      }}
      data-testid="click-k8s-pages-sideoverlay-stackedlayerssideoverlay-handlecopy-46"
      data-clickable="true"
      onClick={handleCopy}
      title="Click to copy command">
      {/* Line Number */}
      <Box
        sx={{
          minWidth: '28px', // Increased width
          height: '24px', // Increased height
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#64748b',

          color: 'white',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          marginTop: '2px',
          flexShrink: 0, // Prevent shrinking
        }}>
        {index + 1}
      </Box>

      {/* Command Text */}
      <Box
        sx={{
          lineHeight: '1.5', // Increased line height
          color: '#1a202c',
          fontWeight: '500',
          overflow: 'hidden', // Fix overflow
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0, // Allow shrinking
          paddingTop: '2px', // Better vertical alignment
        }}>
        <Tooltip title={command}>
          <Text className="SingleLine w-full">{command}</Text>
        </Tooltip>
      </Box>

      {/* Copy Indicator - Improved */}
      {copiedIndex === index && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#10b981',

            color: 'white',
            padding: '8px 16px', // Increased padding
            borderRadius: '8px', // Increased border radius
            fontSize: '12px', // Increased font size
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            zIndex: 10,
            animation: 'fadeIn 0.2s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.9)' },
              '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
            },
          }}>
          ✓ Copied!
        </Box>
      )}
    </Box>
  );
};

// New Component for Commands Display
export const CommandsDisplay = ({ commands }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Parse commands by splitting on && and cleaning up
  // const parsedCommands = useMemo(() => {
  //   if (!commands || commands.length === 0) return [];

  //   // Join all commands and split by &&
  //   const fullCommand = commands.join(' ');
  //   const splitCommands = fullCommand
  //     .split('&&')
  //     .map(cmd => cmd.trim())
  //     .filter(cmd => cmd.length > 0)
  //     .map(cmd => cmd.replace(/\s+/g, ' ')); // Clean up extra spaces

  //   return splitCommands;
  // }, [commands]);

  const handleCopy = index => {
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  if (commands.length === 0) {
    return (
      <Box
        sx={{
          padding: '20px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
          fontStyle: 'italic',
        }}>
        No commands available
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}>
        <Box
          sx={{
            fontSize: '12px',
            color: '#6b7280',
            fontStyle: 'italic',
          }}>
          Click any line to copy
        </Box>
      </Box>

      {/* Commands List */}
      <Box
        sx={{
          maxHeight: '296px',
          width: '100%',
          overflow: 'auto',
          display: 'flex',
          borderRadius: '8px',
          padding: '15px',
          border: '1px solid  var(--content-box-border-color)',
          flexDirection: 'column',
          gap: '8px', // Increased gap between items
        }}
        className="CustomScrollbar">
        {commands.map((command, index) => (
          <CommandLine key={index} command={command} index={index} onCopy={handleCopy} copiedIndex={copiedIndex} />
        ))}
      </Box>
    </Box>
  );
};

const DockerStepLine = ({
  step,
  index,
  copiedIndex,
  isSelected,
  onClick,
  setSelectedLayer,
  handleVulnerabilityPaginationReset,
}) => {
  const bytes = step.size_bytes ?? 0;
  const mb = bytes / (1024 * 1024);
  const size = mb < 1 ? '< 1 MB' : mb.toFixed(2) + ' MB';

  const handleStepClick = useCallback(() => {
    setSelectedLayer(step);
    handleVulnerabilityPaginationReset();
    onClick(index);
  }, [handleVulnerabilityPaginationReset, index, onClick, setSelectedLayer, step]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px', // Increased padding
        minHeight: '48px', // Increased minimum height
        backgroundColor: isSelected ? '#e0f2fe' : '#f8fafc',
        border: isSelected ? '1px solid #0284c7' : '1px solid #e2e8f0',

        borderRadius: '8px',
        fontSize: '13px',
        fontFamily: 'Manrope, sans-serif',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          backgroundColor: isSelected ? '#bae6fd' : '#f1f5f9',
          borderColor: isSelected ? '#0369a1' : '#cbd5e1',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      }}
      data-testid="click-k8s-pages-sideoverlay-stackedlayerssideoverlay-handlestepclick-240"
      data-clickable="true"
      onClick={handleStepClick}>
      {/* Step Number */}
      <Box
        sx={{
          minWidth: '28px', // Increased width
          height: '24px', // Increased height
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isSelected ? '#0284c7' : '#64748b',

          color: 'white',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          flexShrink: 0,
        }}>
        {index + 1}
      </Box>

      {/* Digest */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          color: '#1a202c',
          fontWeight: '500',
          lineHeight: '1.5', // Increased line height
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
        <Tooltip title={`${step.digest}`} isIcon={true}>
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
            }}>
            {step.digest}
          </span>
        </Tooltip>
        {/* Vulnerability Risk Icon */}
      </Box>
      {step.total_vulnerabilities > 0 && <RiskTypeIcons name="Vulnerability" size={18} style={{ marginLeft: 4 }} />}
      {/* Size */}
      <Box
        sx={{
          maWidth: '90px', // Increased width
          textAlign: 'center',
          width: '90px', // Increased width
          fontSize: '12px',
          color: '#64748b',
          fontWeight: '600',
          backgroundColor: 'rgba(100, 116, 139, 0.1)',
          padding: '4px 8px', // Increased padding
          borderRadius: '5px', // Increased border radius
          flexShrink: 0,
        }}>
        {size}
      </Box>

      {/* Copy Indicator */}
      {copiedIndex === index && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#10b981',
            color: 'white',
            padding: '8px 16px', // Increased padding
            borderRadius: '8px', // Increased border radius
            fontSize: '12px', // Increased font size
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            zIndex: 10,
            animation: 'fadeIn 0.2s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.9)' },
              '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
            },
          }}>
          ✓ Copied!
        </Box>
      )}
    </Box>
  );
};

// Updated Docker Build Steps Component
const DockerBuildSteps = ({
  steps,
  setSelectedRowIndex,
  selectedRowIndex,
  setCurrentPage,
  paginationDetails,
  setSelectedLayer,
  handleVulnerabilityPaginationReset,
}) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleCopy = index => {
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleStepClick = index => {
    setSelectedRowIndex(index);
    // setCurrentPage(1);
    // setPageSize(DEFAULT_PAGE_SIZE);
  };

  return (
    <ContentBox
      padding={'0px'}
      style={{
        height: '50vh',
        // padding: '15px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
          padding: '10px 15px',
          paddingBottom: '8px',
          borderBottom: '1px solid #e2e8f0',
        }}>
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
          }}>
          Docker Steps ({steps.length})
        </Box>
      </Box>

      {/* Steps List */}
      <InfiniteScroll
        dataLength={paginationDetails?.total_records ?? steps.length}
        next={() => setCurrentPage(prev => prev + 1)}
        hasMore={paginationDetails?.has_next}
        className="CustomScrollbar"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          padding: '15px',
        }}
        height={400}
        initialScrollY={0}
        loader={
          <div style={{ padding: '15px', display: 'flex', gap: '15px' }}>
            <Skeleton variant="rectangular" width={'100%'} />
            <Skeleton variant="rectangular" width={'100%'} />
            <Skeleton variant="rectangular" width={'100%'} />
          </div>
        }>
        {steps.map((step, idx) => (
          <DockerStepLine
            key={step.id}
            step={step}
            index={idx}
            onCopy={handleCopy}
            copiedIndex={copiedIndex}
            isSelected={selectedRowIndex === idx}
            data-testid={`click-k8s-pages-sideoverlay-stackedlayerssideoverlay-handlestepclick-${idx}`}
            data-clickable="true"
            onClick={handleStepClick}
            handleVulnerabilityPaginationReset={handleVulnerabilityPaginationReset}
            setSelectedLayer={setSelectedLayer}
          />
        ))}
      </InfiniteScroll>
    </ContentBox>
  );
};

const StackedLayersSideoverlay = ({ selectedRow, isWorkload = false }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const [vulCurrentPage, setVulCurrentPage] = useState(1);
  const [vulPageSize, setVulPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedLayer, setSelectedLayer] = useState({});
  const [allLayersData, setAllLayersData] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  const {
    data: imageLayers,
    isFetching: isFetchingImageLayers,
    isLoading: isLoadingImageLayers,
  } = useEnhancedQuery(
    ['get_images_layers', selectedRow, currentPage],
    () =>
      agent.Kubernetes.get_images_layers({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        image_name: selectedRow?.name,
        rows_per_page: 10,
        page_number: currentPage,
        // export: false,
      }),
    {
      enabled: !isWorkload,
    },
  );

  // console.log(selectedRow?.image_name, 'isFetchingLayersVulnerability');

  const {
    data: workloadImageLayers,
    isFetching: isFetchingWorkloadImageLayers,
    isLoading: isLoadingWorkloadLayers,
  } = useEnhancedQuery(
    ['get_workload_image_layers', selectedRow, currentPage],
    () =>
      agent.Vulnerabilites.get_workload_images_layers({
        account_id: selectedRow?.account_id,
        cluster_name: selectedRow.cluster_name,
        // image_id: selectedRow?.image_name,
        image_id: selectedRow.image_id,
        rows_per_page: 10,
        page_number: currentPage,
        // export: false,
      }),
    {
      enabled: isWorkload,
    },
  );

  const stepsData = useMemo(() => {
    let layers = [];
    if (isWorkload) {
      if (isFetchingImageLayers) return [];
      layers =
        workloadImageLayers?.data?.layers?.map(item => ({
          ...item,
          layer_id: item.digest,
        })) || [];
    } else {
      if (isFetchingWorkloadImageLayers) return [];
      layers = imageLayers?.data?.layers?.data || [];
    }
    // Sort by total_vulnerabilities ascending
    const sorted = [...layers].sort((a, b) => (b.total_vulnerabilities ?? 0) - (a.total_vulnerabilities ?? 0));

    return (
      sorted?.map((item, idx) => ({
        ...item,
        index: idx,
      })) || []
    );
  }, [
    imageLayers?.data?.layers?.data,
    isFetchingImageLayers,
    isFetchingWorkloadImageLayers,
    isWorkload,
    workloadImageLayers?.data?.layers,
  ]);

  const { data: workloadImageLayersVulns, isFetching: isFetchingWorkloadImageLayersVulns } = useEnhancedQuery(
    ['get_workload_image_layers_vuln', selectedRow, vulPageSize, vulCurrentPage, selectedLayer],
    () =>
      agent.Vulnerabilites.get_workload_images_layers_vulns({
        image_id: selectedRow?.image_id,
        account_id: selectedRow?.account_id,
        cluster_name: selectedRow.cluster_name,
        layer_id: isEmpty(selectedLayer) ? stepsData[0]?.layer_id : selectedLayer?.layer_id,
        page_number: vulCurrentPage,
        rows_per_page: vulPageSize,
        // export: false,
      }),
    {
      enabled: isWorkload && !!stepsData && !isFetchingWorkloadImageLayers,
    },
  );

  const effectiveLayerId = isEmpty(selectedLayer) ? stepsData[0]?.layer_id : selectedLayer?.layer_id;

  const { data: layersVulnerability, isFetching: isFetchingLayersVulnerability } = useEnhancedQuery(
    ['get_images_layers_vuln', selectedRow, vulPageSize, vulCurrentPage, selectedLayer, stepsData[0]?.layer_id],
    () =>
      agent.Kubernetes.get_images_layers_vuln({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        layer_id: effectiveLayerId,
        image_name: selectedRow?.name,
        rows_per_page: vulPageSize,
        page_number: vulCurrentPage,
        // export: false,
      }),
    {
      enabled: !!effectiveLayerId && !isWorkload && !!stepsData && !isFetchingImageLayers,
    },
  );

  const isLoadingResponse = useMemo(() => {
    if (isWorkload) return isLoadingWorkloadLayers;
    return isLoadingImageLayers;
  }, [isLoadingImageLayers, isLoadingWorkloadLayers, isWorkload]);

  const isFetchingVulns = useMemo(() => {
    if (isWorkload) {
      return isFetchingWorkloadImageLayersVulns;
    }
    return isFetchingLayersVulnerability;
  }, [isFetchingLayersVulnerability, isFetchingWorkloadImageLayersVulns, isWorkload]);

  useEffect(() => {
    if (isFetchingImageLayers || isFetchingWorkloadImageLayers) return;
    setAllLayersData(prev => {
      // Get all existing layer_ids
      const existingLayerIds = new Set(prev.map(item => item.layer_id));
      // Filter stepsData to only include new layer_ids
      const newItems = stepsData?.filter(item => !existingLayerIds.has(item.layer_id));
      // Combine and re-index
      const allData = [...prev, ...newItems].map((item, idx) => ({
        ...item,
        id: idx + 1,
      }));
      return allData;
    });
  }, [isFetchingImageLayers, isFetchingWorkloadImageLayers, stepsData]);

  // console.log(allLayersData, 'allLayersData');
  const paginationDetails = useMemo(() => {
    if (isWorkload) {
      if (isFetchingWorkloadImageLayers) return {};
      return {
        total_records: workloadImageLayers?.data?.total_layers,
        has_next: currentPage * 10 < workloadImageLayers?.data?.total_layers,
      };
    } else {
      if (isFetchingImageLayers) return [];
      return imageLayers?.data?.layers?.pagination;
    }
  }, [
    currentPage,
    imageLayers?.data?.layers?.pagination,
    isFetchingImageLayers,
    isFetchingWorkloadImageLayers,
    isWorkload,
    workloadImageLayers?.data?.total_layers,
  ]);

  // console.log(paginationDetails, 'paginationDetails');

  const rowsData = useMemo(() => {
    if (isWorkload) {
      if (isFetchingWorkloadImageLayersVulns) return [];
      return workloadImageLayersVulns?.data?.vulnerabilities ?? [];
    }
    if (isFetchingLayersVulnerability) return [];
    return layersVulnerability?.data?.vulnerabilities?.data ?? [];
  }, [
    isFetchingLayersVulnerability,
    isFetchingWorkloadImageLayersVulns,
    isWorkload,
    layersVulnerability?.data?.vulnerabilities?.data,
    workloadImageLayersVulns?.data?.vulnerabilities,
  ]);

  const totalSize = useMemo(() => {
    if (isWorkload) {
      if (isFetchingWorkloadImageLayersVulns) return 0;
      return workloadImageLayersVulns?.data?.pagination?.total_records ?? 0;
    }

    if (isFetchingLayersVulnerability) return 0;
    return layersVulnerability?.data?.vulnerabilities?.pagination?.total_records ?? 0;
  }, [
    isFetchingLayersVulnerability,
    isFetchingWorkloadImageLayersVulns,
    isWorkload,
    layersVulnerability?.data?.vulnerabilities?.pagination?.total_records,
    workloadImageLayersVulns?.data?.pagination.total_records,
  ]);

  const handleVulnerabilityPaginationReset = useCallback(() => {
    setVulPageSize(DEFAULT_PAGE_SIZE);
    setVulCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback(_pageSize => {
    setVulCurrentPage(1);
    setVulPageSize(_pageSize);
  }, []);

  const commands = useMemo(() => {
    if (isFetchingVulns) return [];
    const command = isEmpty(selectedLayer)
      ? imageLayers?.data?.layers?.data?.length > 0
        ? imageLayers?.data?.layers?.data?.[0]?.command
        : ''
      : selectedLayer?.command;
    if (!command) return [];

    // Split on: semicolon OR tab OR 2+ spaces (with optional whitespace around them)
    return command
      ?.split(/\s*[;\t]\s*|\s{4,}/)
      ?.map(cmd => cmd.trim())
      ?.filter(cmd => cmd.length > 0);
  }, [imageLayers?.data?.layers?.data, isFetchingVulns, selectedLayer]);

  // console.log(commands, 'commands')
  // if (isWorkload) return <></>;
  return (
    <Box display="flex" flexDirection="row" gap={'30px'}>
      <div
        style={{
          width: '100%',
          height: 'calc( 100vh - 200px )',
          padding: '15px',
          display: 'flex',
          gap: '25px',
        }}>
        {isLoadingResponse ? (
          <div
            style={{
              width: '40%',
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
            }}>
            {Array.from(new Array(15)).map((_, idx) => (
              <Skeleton variant="rectangular" key={idx} height={25} width={'100%'} sx={{ borderRadius: '8px' }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '40%' }}>
            <DockerBuildSteps
              steps={allLayersData}
              setSelectedRowIndex={setSelectedRowIndex}
              selectedRowIndex={selectedRowIndex}
              setCurrentPage={setCurrentPage}
              paginationDetails={paginationDetails}
              setSelectedLayer={setSelectedLayer}
              handleVulnerabilityPaginationReset={handleVulnerabilityPaginationReset}
            />

            {/* Updated Commands Section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <CommandsDisplay commands={commands} />
            </div>
          </div>
        )}

        <div style={{ width: '60%', height: '100%' }}>
          <MuiTable
            rows={
              rowsData?.map((item, idx) => {
                return {
                  ...item,
                  id: idx + 1,
                };
              }) ?? []
            }
            customClassName={'height-full'}
            columns={stackedLayersColumn()}
            total_size={totalSize ?? 0}
            rowHeight={49.9}
            LoaderState={isFetchingVulns}
            isDynamicHeight={true}
            tableHeight={'69vh'}
            onPageChange={setVulCurrentPage}
            onPageSizeChange={handlePageSizeChange}
            noDataMessage={'No Vulnerabilities present!'}
            paginationMode={'server'}
            sortingMode="server"
            page={vulCurrentPage}
            pageSize={vulPageSize}
          />
        </div>
      </div>
    </Box>
  );
};

export default StackedLayersSideoverlay;
